import { useCallback, useState } from 'react'
import type {
  Contact,
  ContactDetail,
  CreateContactPayload,
  DuplicateGroup,
  MergeContactsPayload,
  UpdateContactPayload,
} from 'types/contacts'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface ContactsParams {
  skip?: number
  take?: number
  includeInactive?: boolean
  q?: string
}

const unwrapContacts = (response: { data: Contact[] }) => response.data

export function useContacts(params?: ContactsParams): QueryResult<Contact[]> {
  return useApiQuery(`/api/contacts${qs(params)}`, { select: unwrapContacts })
}

export function useContact(id: string | undefined): QueryResult<ContactDetail> {
  return useApiQuery<ContactDetail>(id ? `/api/contacts/${id}` : null)
}

export function useCreateContact(): MutationResult<Contact, [CreateContactPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateContactPayload): Promise<Contact> => {
      setLoading(true)
      try {
        return await fetchApi<Contact>('/api/contacts', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateContact(): MutationResult<Contact, [string, UpdateContactPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateContactPayload): Promise<Contact> => {
      setLoading(true)
      try {
        return await fetchApi<Contact>(`/api/contacts/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteContact(): MutationResult<Contact, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<Contact> => {
      setLoading(true)
      try {
        return await fetchApi<Contact>(`/api/contacts/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export interface ImportResult {
  created: number
  updated: number
  total: number
  errors: string[]
}

export interface BulkImportContact {
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  organizationName?: string
  notes?: string
  ordoUserId?: string
  timezone?: string
  extraData?: Record<string, unknown>
  schoolLocations?: { externalId: string; role: string }[]
  vendorLocations?: { externalId: string; role: string }[]
  organizations?: { externalId: string; role: string }[]
}

export function useBulkImportContacts(): MutationResult<ImportResult, [BulkImportContact[]]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (contacts: BulkImportContact[]): Promise<ImportResult> => {
      setLoading(true)
      try {
        return await fetchApi<ImportResult>('/api/contacts/bulk-import', {
          method: 'POST',
          body: JSON.stringify({ contacts }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useMergeContacts(): MutationResult<ContactDetail, [string, MergeContactsPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (targetId: string, payload: MergeContactsPayload): Promise<ContactDetail> => {
      setLoading(true)
      try {
        return await fetchApi<ContactDetail>(`/api/contacts/${targetId}/merge`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export type AssociationKind = 'school-locations' | 'vendor-locations' | 'organizations'

export function useAddContactAssociation(): MutationResult<unknown, [string, AssociationKind, string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (contactId: string, kind: AssociationKind, externalId: string, role: string) => {
      setLoading(true)
      try {
        return await fetchApi(`/api/contacts/${contactId}/${kind}`, {
          method: 'POST',
          body: JSON.stringify({ externalId, role }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateContactAssociation(): MutationResult<unknown, [string, AssociationKind, string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (contactId: string, kind: AssociationKind, associationId: string, role: string) => {
      setLoading(true)
      try {
        return await fetchApi(`/api/contacts/${contactId}/${kind}/${associationId}`, {
          method: 'PUT',
          body: JSON.stringify({ role }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useRemoveContactAssociation(): MutationResult<unknown, [string, AssociationKind, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (contactId: string, kind: AssociationKind, associationId: string) => {
      setLoading(true)
      try {
        return await fetchApi(`/api/contacts/${contactId}/${kind}/${associationId}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDuplicateContacts(): QueryResult<DuplicateGroup[]> {
  return useApiQuery<DuplicateGroup[]>('/api/contacts/duplicates')
}
